import { useCallback, useState } from "react";
import {
  PROJECT_CONTRACT_KEYS,
  PROJECT_CONTRACTS,
} from "../../../constants/projectContracts";
import {
  isProjectContractSigned,
  saveProjectContractSignature,
} from "../../../components/utils/projectContractSignatures";

const YT_CONTRACT_KEY = PROJECT_CONTRACT_KEYS.YOUTUBE_LICENSE;
const YT_CONTRACT_CONFIG = PROJECT_CONTRACTS[YT_CONTRACT_KEY];

export function applyYoutubeLicenseSignatureToProject(
  project,
  uid,
  signerName
) {
  const name = (signerName || "").trim();
  return {
    ...project,
    contractSignatures: {
      ...(project.contractSignatures || {}),
      [YT_CONTRACT_KEY]: {
        ...(project.contractSignatures?.[YT_CONTRACT_KEY] || {}),
        [uid]: { signerName: name, signedAt: new Date() },
      },
    },
  };
}

/**
 * Shared YouTube license contract state + sign handler for project and analytics pages.
 * @param {{ projectId: string, project: object, uid: string, hasYoutube: boolean, bypassContractGate?: boolean }} opts
 */
export function useYoutubeLicenseContract({
  projectId,
  project,
  uid,
  hasYoutube,
  bypassContractGate = false,
}) {
  const [signingContract, setSigningContract] = useState(false);
  const contractSigned = isProjectContractSigned(project, YT_CONTRACT_KEY, uid);
  const contractRequired = Boolean(hasYoutube);
  const contractGateActive =
    contractRequired && !contractSigned && !bypassContractGate;
  const canViewPage = !contractRequired || contractSigned || bypassContractGate;

  const handleContractSign = useCallback(
    async (signerName) => {
      if (!projectId || !uid) return;
      setSigningContract(true);
      try {
        const name = signerName.trim();
        await saveProjectContractSignature(
          projectId,
          YT_CONTRACT_KEY,
          uid,
          name
        );
        return name;
      } finally {
        setSigningContract(false);
      }
    },
    [projectId, uid]
  );

  return {
    contractConfig: YT_CONTRACT_CONFIG,
    contractSigned,
    contractRequired,
    contractGateActive,
    canViewPage,
    contractSigning: signingContract,
    handleContractSign,
  };
}
